import { Box, FormLabel, Stack, TextField } from '@mui/material';
import { Typography } from '@sweep-io/sweep-design';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';
import { RolesDropdown } from './RolesDropdown';
import { UserDefinedRoleGroupsInterface } from '@server/role-group-interface';
import { useInviteForm } from './useInviteForm';
import { MessageResponse } from './InviteUserInput';

interface InviteUsersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  roles: UserDefinedRoleGroupsInterface[];
  onSendInvitationEmail: (email: string, role: string, message: string) => Promise<any>;
  afterSendActions: (isError: boolean, messageResponse: MessageResponse) => Promise<void>;
  messagePlaceholder?: string;
  disableRoleDropdown?: boolean;
}

export const InviteUsersDialog = ({
  isOpen,
  onClose,
  roles,
  onSendInvitationEmail,
  afterSendActions,
  messagePlaceholder = `E.g. Hi, let's work together on Sweep 🌟`,
  disableRoleDropdown,
}: InviteUsersDialogProps) => {
  const {
    email,
    setEmail,
    message,
    setMessage,
    selectedRole,
    selectRoleById,
    isInvitationSending,
    sendInvitationEmail,
    isEmailValid,
    clearState,
  } = useInviteForm({ roles, onSendInvitationEmail, afterSendActions });

  const handleClose = () => {
    clearState();
    onClose();
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      handleClose={handleClose}
      title="Invite users"
      confirmText="Invite"
      cancelText="Cancel"
      onConfirm={sendInvitationEmail}
      onCancel={handleClose}
      disableConfirmButton={!isEmailValid || isInvitationSending}
      PaperProps={{ sx: { width: '600px' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <FormLabel>
            <Typography variant="caption">Email</Typography>
          </FormLabel>
          <TextField
            sx={{
              width: '100%',
              '& .MuiInputBase-adornedEnd': {
                pr: 0,
              },
            }}
            placeholder="Enter email"
            value={email}
            onChange={(event) => setEmail(event?.target.value ?? '')}
            InputProps={{
              endAdornment: (
                <Stack sx={{ width: '160px', alignItems: 'flex-end' }}>
                  <RolesDropdown
                    onChangeRoleCb={(_id, roleId) => selectRoleById(roleId)}
                    accountRoles={roles}
                    disableRoleDropdown={disableRoleDropdown}
                    userRole={selectedRole}
                  />
                </Stack>
              ),
            }}
          />
        </Box>

        <Box>
          <FormLabel>
            <Typography variant="caption">Message</Typography>
          </FormLabel>
          <TextField
            multiline
            rows={4}
            sx={{ width: '100%', height: '133px' }}
            placeholder={messagePlaceholder}
            value={message}
            onChange={(event) => setMessage(event?.target.value ?? '')}
          />
        </Box>
      </Box>
    </ConfirmDialog>
  );
};
